import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useHelpChat } from '@/components/help/HelpChatContext'
import {
  ChevronDown,
  PlayCircle,
  PhoneOutgoing,
  RotateCw,
  Wand2,
  WalletCards,
  Link as LinkIcon,
  HelpCircle,
  ArrowRight,
  MessageSquare,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// AffiliateWalkthroughPage
//
// Onboarding home for new affiliates. Three sections:
//   1. Getting started — visual map of the three surfaces (dashboard, demo
//      workspace, public preview) plus a "what does success look like" line
//   2. Step-by-step demo flow — numbered cards mirroring help-kb/affiliate/
//      demo-workflow.md
//   3. FAQ — expandable accordion drawing from the rest of the affiliate KB
//
// Bottom CTA: "Still stuck? Ask LegalLady" opens the in-app help assistant.
// Mobile-first layout: 1-column on phones, expands to 2-3 cols on desktop.
// ─────────────────────────────────────────────────────────────────────────────

interface Step {
  n: number
  icon: React.ComponentType<{ className?: string }>
  title: string
  body: string
  cta?: { label: string; to: string }
}

const STEPS: Step[] = [
  {
    n: 1,
    icon: PlayCircle,
    title: 'Open the public preview',
    body: 'Click "Open preview" on your dashboard. A new tab opens at /preview/<your-slug> showing the polished law-firm landing page your prospect will see, with the chat widget already popped open.',
    cta: { label: 'Open my preview', to: '/affiliate' },
  },
  {
    n: 2,
    icon: Wand2,
    title: 'Run the AI Agent Wizard for the prospect',
    body: 'Switch into your demo workspace from the org-switcher in the header. Go to AI Agents and either edit the existing demo agent or create a new one. Paste the prospect\'s firm URL — the wizard scrapes their site and builds a firm-specific inbound + outbound agent in about 60 seconds.',
    cta: { label: 'Open AI Agents', to: '/agents' },
  },
  {
    n: 3,
    icon: PhoneOutgoing,
    title: 'Demo inbound and outbound',
    body: 'Inbound: have the prospect call your demo phone number from their cell. The AI answers using their firm name. Outbound: from the affiliate dashboard, click "Trigger demo outbound" and enter their phone — the AI calls them within 5 seconds.',
    cta: { label: 'Trigger a demo call', to: '/affiliate' },
  },
  {
    n: 4,
    icon: WalletCards,
    title: 'Send your affiliate link',
    body: 'On the affiliate dashboard, click Copy on your affiliate link and paste it into the chat or email. The 90-day attribution cookie tags the prospect to you. Once they sign up and pay an invoice, commission shows on your ledger.',
    cta: { label: 'Copy my link', to: '/affiliate' },
  },
  {
    n: 5,
    icon: RotateCw,
    title: 'Reset for the next prospect',
    body: 'After the call, hit "Reset for next prospect". Wipes the wizard config + transcripts; keeps your demo phone number + sample leads. Ready to repeat for the next pitch in 15 seconds.',
    cta: { label: 'Reset my demo', to: '/affiliate' },
  },
]

interface FAQItem {
  q: string
  a: string
}

const FAQ: FAQItem[] = [
  {
    q: 'How do I find my commission?',
    a: 'On the affiliate dashboard at /affiliate. The four cards at the top show Referrals, Converted, Pending, and Paid. Scroll to "Commission ledger" for the row-by-row breakdown. Commission only fires on paid invoices — referrals on free trials show in Referrals but won\'t produce commission rows until they convert to paid.',
  },
  {
    q: 'How do I get to the demo workspace?',
    a: 'Click your org name in the top-right of the dashboard, then select your demo workspace from the dropdown. That switches your context — every page (Leads, Calls, Agents, etc.) now shows your sandbox\'s data instead of your personal account.',
  },
  {
    q: 'How do I create my agent for a prospect?',
    a: 'Inside your demo workspace, go to /agents. Click "Edit Agent" on the existing demo agent (or "+ New Agent" if you\'ve already reset). Paste the prospect\'s firm URL into Step 1, wait ~30 seconds for the scrape, review what was found, and save. The wizard builds both inbound and outbound voice agents bound to your demo phone number.',
  },
  {
    q: 'My prospect\'s website didn\'t scrape correctly. What do I do?',
    a: 'Some sites block automated scrapers (Cloudflare, etc.). Two options: (1) Try a different page URL like the firm\'s About or Contact page instead of the homepage. (2) Manually configure Step 2 of the wizard — type firm details directly without auto-fill.',
  },
  {
    q: 'The chat widget doesn\'t open on the preview page. Why?',
    a: 'The widget auto-open setting is applied during reset. If you\'ve been changing things in your sandbox without resetting, hit "Reset for next prospect" once and reload the preview. The mic icon in the chat header (for browser voice) is also enabled at reset time.',
  },
  {
    q: 'Trigger demo outbound says "phone number invalid". What\'s the format?',
    a: 'The form accepts most US formats: (310) 555-0144, 310-555-0144, +1 310 555 0144. Bare 10-digit US numbers automatically get a +1 added. International numbers require a + and country code, e.g., +44 20 7946 0958.',
  },
  {
    q: 'My referral signed up but I see $0 in Pending.',
    a: 'They\'re probably on free trial. Commission only fires on paid invoices, not signups. Once Stripe processes their first paid invoice, a Pending row will appear within 24 hours.',
  },
  {
    q: 'How long does my attribution last?',
    a: 'A 90-day cookie is set when someone clicks your link. If they sign up within 90 days, you\'re permanently attributed to them for the duration of your commission term (default 12 months from first paid invoice, or unlimited if your contract specifies that).',
  },
  {
    q: 'My prospect needs Spanish. Can I demo that?',
    a: 'The wizard exposes Spanish as a language option, but Spanish setup uses a different voice + transcription stack and requires a manual support handoff to dial in correctly. For the demo, run the agent in English. After they sign up, email support@legallady.ai and we\'ll convert their agent to Spanish (usually same day).',
  },
  {
    q: 'When do I get paid out?',
    a: 'Commission rows go through three states: Pending → Approved → Paid. A platform admin reviews and approves Pending entries, then approved entries get bundled into a monthly payout batch. Payment goes via wire/ACH to the bank info on your application.',
  },
  {
    q: 'Can I get a different affiliate link for a specific campaign?',
    a: 'Yes — you can have multiple links labeled by campaign (e.g., "linkedin-q2", "podcast"). Email support@legallady.ai with your affiliate id and the campaign label and we\'ll spin up a separate tracked link.',
  },
  {
    q: 'Something is broken and I can\'t finish a demo. Who do I contact?',
    a: 'Email support@legallady.ai. Include your affiliate id (visible at the bottom of /affiliate) and a one-line description of what broke. For real-time help during a live demo, the in-app chat (bottom-right of every page) goes to LegalLady AI which can answer most affiliate questions.',
  },
]

// Three-card "your three surfaces" map at the top of the page
const SURFACES = [
  {
    title: 'Affiliate Dashboard',
    href: '/affiliate',
    icon: WalletCards,
    desc: 'Your home base. Track referrals, copy your affiliate link, see commission status, trigger demo calls, and reset between prospects.',
    accent: 'border-brand-royal/30',
  },
  {
    title: 'Demo Workspace',
    href: '/agents',
    icon: Wand2,
    desc: 'A fully working LegalLady org with sample data. Switch into it from the org-switcher to run the AI Agent Wizard for a prospect\'s firm.',
    accent: 'border-amber-300/40',
  },
  {
    title: 'Public Preview',
    href: '/affiliate',
    icon: PlayCircle,
    desc: 'A polished law-firm landing page at /preview/<slug> with the chat widget embedded. This is what your prospects see during a live demo.',
    accent: 'border-emerald-300/30',
  },
]

function FAQRow({ item, defaultOpen = false }: { item: FAQItem; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-border/60 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start justify-between gap-4 py-4 text-left group"
        aria-expanded={open}
      >
        <span className="text-sm font-medium pr-4 group-hover:text-brand-royal transition-colors">{item.q}</span>
        <ChevronDown
          className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{
          maxHeight: open ? '400px' : '0px',
          opacity: open ? 1 : 0,
        }}
      >
        <div className="pb-4 pr-8 text-sm text-muted-foreground leading-relaxed">
          {item.a}
        </div>
      </div>
    </div>
  )
}

export default function AffiliateWalkthroughPage() {
  const { openHelp } = useHelpChat()
  return (
    <div className="space-y-8 pb-12">
      <PageHeader
        eyebrow="Affiliate"
        title="Walkthrough & FAQ"
        subtitle="Everything you need to demo LegalLady, refer firms, and find your commission."
      />

      {/* ── Section 1 · Your three surfaces ───────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Getting started</p>
            <h2 className="text-lg font-semibold">Your three surfaces</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SURFACES.map(s => {
            const Icon = s.icon
            return (
              <Card key={s.title} className={`card-inset border ${s.accent}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="h-4 w-4 text-brand-royal" />
                    <span className="text-sm font-semibold">{s.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{s.desc}</p>
                  <Button asChild variant="ghost" size="sm" className="h-auto p-0 text-xs text-brand-royal hover:text-brand-royal/80 hover:bg-transparent">
                    <Link to={s.href}>
                      Go there <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </section>

      {/* ── Section 2 · Step-by-step demo flow ────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">The demo</p>
            <h2 className="text-lg font-semibold">Run a live demo in five steps</h2>
            <p className="text-xs text-muted-foreground mt-1">15 minutes from "hello" to "here's my link." Each step builds on the last.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {STEPS.map(step => {
            const Icon = step.icon
            return (
              <Card key={step.n} className="card-inset">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-royal/10 text-brand-royal text-xs font-semibold ring-1 ring-brand-royal/20">
                      {step.n}
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-sm">{step.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-5">
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">{step.body}</p>
                  {step.cta && (
                    <Button asChild variant="outline" size="sm" className="h-8 text-xs">
                      <Link to={step.cta.to}>
                        {step.cta.label} <ArrowRight className="h-3 w-3 ml-1.5" />
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </section>

      {/* ── Section 3 · FAQ accordion ─────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Frequently asked</p>
            <h2 className="text-lg font-semibold">Answers to the questions sellers ask most</h2>
          </div>
        </div>
        <Card className="card-inset">
          <CardContent className="px-5 py-2">
            {FAQ.map((item, idx) => (
              <FAQRow key={item.q} item={item} defaultOpen={idx === 0} />
            ))}
          </CardContent>
        </Card>
      </section>

      {/* ── Bottom CTA · Ask LegalLady ────────────────────────────────── */}
      <Card className="card-inset border-brand-royal/20 bg-gradient-to-br from-brand-royal/5 via-transparent to-transparent">
        <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="h-4 w-4 text-brand-royal" />
              <span className="text-xs uppercase tracking-wider font-semibold text-brand-royal">Still stuck?</span>
            </div>
            <CardTitle className="text-base sm:text-lg mb-1.5">Ask LegalLady</CardTitle>
            <CardDescription className="text-sm">
              The in-app assistant has read every help doc in the system, including all the affiliate articles.
              Ask anything: <em>"how do I trigger a demo outbound,"</em> <em>"where do I see paid commission,"</em> <em>"my wizard scrape failed."</em>
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <Button onClick={openHelp} className="w-full sm:w-auto bg-brand-royal hover:bg-brand-royal/90 text-white">
              <MessageSquare className="h-4 w-4 mr-2" />
              Open chat
            </Button>
            <Button asChild variant="ghost" size="sm" className="w-full sm:w-auto h-auto p-0 text-xs text-muted-foreground hover:text-foreground hover:bg-transparent">
              <a href="mailto:support@legallady.ai?subject=Affiliate%20question">
                <LinkIcon className="h-3 w-3 mr-1" /> Or email support
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
